import React from "react";
import classNames from "classnames";
import { Typography } from "../../Typography/Typography";
import "./Checkbox.scss";
import { Svg } from "../../Svg/Svg";

/**
 * Enum representing the available checkbox display types.
 *
 * @description Defines the visual style variants for the Checkbox component.
 *
 * @property {string} general - Standard checkbox with label text
 * @property {string} selectBg - Checkbox with background selection styling
 * @property {string} withoutSelectBg - Checkbox without background selection styling
 * @property {string} withImg - Checkbox with image/icon instead of text label
 */
export enum CBType {
  general = "general",
  selectBg = "selectBg",
  withoutSelectBg = "withoutSelectBg",
  withImg = "withImg",
}

/**
 * Props for the Checkbox component.
 *
 * @description The Checkbox component is a customizable checkbox input with multiple
 * display variants and integrated label support.
 *
 * @property {string} title - The label text displayed next to the checkbox. Required.
 * @property {string} name - The name attribute for the checkbox input. Required.
 * @property {string} [id] - HTML id attribute for the checkbox input.
 * @property {keyof typeof CBType} [type="general"] - The visual style variant of the checkbox.
 *   - "general": Standard checkbox with label text
 *   - "selectBg": Checkbox with background selection styling
 *   - "withoutSelectBg": Checkbox without background selection styling
 *   - "withImg": Checkbox with image/icon instead of text label
 * @property {boolean} [disabled=false] - Whether the checkbox is disabled and non-interactive.
 * @property {boolean} [checked=false] - Whether the checkbox is currently checked.
 * @property {(event: React.ChangeEvent<HTMLInputElement>, type?: any) => void} [onChange] - Change event handler.
 *   Called when the checkbox state changes. Receives the event and the checkbox type.
 * @property {() => void} [onClick] - Click event handler. Called when the checkbox is clicked.
 * @property {(event: React.KeyboardEvent<HTMLInputElement>, type?: any) => void} [onKeyPress] - Keyboard event handler.
 *   Called on key press, primarily for Enter key handling.
 * @property {string} [className] - Additional CSS class names to apply to the checkbox container.
 * @property {string} [textClassName] - CSS class names to apply to the label text.
 * @property {string} [svgIcon] - Name of the icon to display (used with "withoutSelectBg" and "withImg" types).
 * @property {string} [testId] - Data-testid attribute for testing purposes (container).
 * @property {string} [checkboxTestid] - Data-testid attribute for the checkbox input element.
 * @property {string} [toolTip] - Tooltip text displayed on hover.
 *
 * @example
 * // General checkbox
 * <Checkbox
 *   title="Accept terms and conditions"
 *   name="terms"
 *   checked={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * />
 *
 * @example
 * // Checkbox with background selection styling
 * <Checkbox
 *   title="Select all items"
 *   name="selectAll"
 *   type="selectBg"
 *   checked={selectAll}
 *   onChange={handleSelectAll}
 * />
 *
 * @example
 * // Checkbox with icon
 * <Checkbox
 *   title="Favorite"
 *   name="favorite"
 *   type="withImg"
 *   svgIcon="heart"
 *   checked={isFavorite}
 *   onChange={handleFavorite}
 * />
 */
interface Props {
  title: string;
  name: string;
  id?: string;
  type?: keyof typeof CBType;
  disabled?: boolean;
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, type?: any) => void;
  onClick?: () => void | undefined;
  onKeyPress?: (
    event: React.KeyboardEvent<HTMLInputElement>,
    type?: any
  ) => void;
  className?: string;
  textClassName?: string;
  svgIcon?: string;
  testId?: string;
  checkboxTestid?: string;
  toolTip?: string;
}

/**
 * A customizable checkbox component with multiple display variants.
 *
 * @description Checkbox renders a styled checkbox input with integrated label support.
 * It supports multiple visual variants through the CBType enum, including standard
 * checkboxes, checkboxes with background selection styling, and image-based checkboxes.
 *
 * Features:
 * - Multiple display variants (general, selectBg, withoutSelectBg, withImg)
 * - Integrated label with Typography component
 * - Disabled state support
 * - Keyboard navigation support (Enter key)
 * - Icon support for visual variants
 * - Tooltip support
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A labeled checkbox element with the configured variant
 *
 * @see Props for all available properties
 * @see CBType for available checkbox types
 */
export const Checkbox: React.FC<Props> = (props) => {
  const {
    id,
    title = "",
    name,
    disabled = false,
    onClick,
    onChange,
    onKeyPress,
    checked = false,
    type = CBType[CBType.general],
    className = "",
    textClassName = "",
    svgIcon = "",
    testId,
    checkboxTestid,
    toolTip,
  } = props;

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event, type);
    }
  };

  const handleOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyPress) {
      if (event.key === "Enter") {
        onKeyPress(event, type);
      }
    }
  };

  const wrapClass = classNames("checkbox", className, {
    [`checkbox--${type}`]: !!type,
    [`checkbox--${type}--selected`]: !!checked,
    [`checkbox--${type}--disabled`]: !!disabled,
    "no-text": !title,
  });

  return (
    <>
      {/* GENERAL Check box */}
      {type === CBType[CBType.general] && (
        <label className={wrapClass} data-testid={testId}>
          <input
            data-testid={checkboxTestid}
            id={id}
            type="checkbox"
            name={name}
            checked={checked}
            disabled={disabled}
            onClick={!disabled ? handleOnClick : undefined}
            onChange={!disabled ? handleOnChange : undefined}
            onKeyDown={!disabled ? handleOnKeyPress : undefined}
          ></input>
          <Typography
            component="span"
            variant="body2"
            className={textClassName}
          >
            {title}
          </Typography>
        </label>
      )}

      {type === CBType[CBType.selectBg] && (
        <label className={wrapClass} data-testid={testId}>
          <Typography
            component="span"
            variant="body2"
            className={textClassName}
          >
            {title}
          </Typography>
          <input
            id={id}
            type="checkbox"
            name={name}
            checked={checked}
            disabled={disabled}
            onClick={!disabled ? handleOnClick : undefined}
            onChange={!disabled ? handleOnChange : undefined}
          ></input>
        </label>
      )}

      {type === CBType[CBType.withoutSelectBg] && (
        <label className={wrapClass} data-testid={testId}>
          <Typography
            component="span"
            variant="body2"
            className={textClassName}
          >
            {title}
          </Typography>
          <input
            id={id}
            type="checkbox"
            name={name}
            checked={checked}
            disabled={disabled}
            onClick={!disabled ? handleOnClick : undefined}
            onChange={!disabled ? handleOnChange : undefined}
            onKeyDown={!disabled ? handleOnKeyPress : undefined}
          ></input>
          {svgIcon !== "" && <Svg icon={svgIcon} className="checkboxGoIcon" />}
        </label>
      )}

      {type === CBType[CBType.withImg] && (
        <label className={wrapClass} data-testid={testId} title={toolTip}>
          <input
            id={id}
            type="checkbox"
            name={name}
            checked={checked}
            disabled={disabled}
            onClick={!disabled ? handleOnClick : undefined}
            onChange={!disabled ? handleOnChange : undefined}
            onKeyDown={!disabled ? handleOnKeyPress : undefined}
          ></input>
          <Svg icon={svgIcon} className="checkboxImage" title={toolTip} />
        </label>
      )}
    </>
  );
};
